import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/godsforge";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const godsforge: CaseStudy = {
  slug: "godsforge",
  title: "GODSFORGE",
  cardTitle: "Разработка интернет-магазина ювелирного бренда GODSFORGE",
  subtitle: "Ювелирный бренд",
  industry: { label: "Ритейл и аксессуары", slug: "retail" },
  eyebrow: "Интернет-магазин · Ювелирные изделия",
  h1: "Разработка интернет-магазина ювелирного бренда GODSFORGE",
  lead:
    "Разработали интернет-магазин ювелирного бренда GODSFORGE: каталог талисманов, коллекционных, индивидуальных и мужских изделий, страницу автора коллекций, карточку изделия с корзиной и личный кабинет.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить премиальный интернет-магазин" },

  seo: {
    primaryKeyword: "разработка интернет-магазина ювелирного бренда",
    secondaryKeywords: [
      "сайт ювелирного бренда",
      "интернет-магазин украшений разработка",
      "разработка премиального e-commerce",
      "сайт ювелирного дома",
    ],
    semanticEntities: [
      "каталог украшений",
      "коллекционные изделия",
      "индивидуальные изделия",
      "мужские украшения",
      "карточка изделия",
      "корзина",
      "личный кабинет",
      "история мастера",
    ],
    excludedKeywords: [
      "купить кольцо",
      "золотые серьги",
      "ювелирные изделия купить",
      "подвеска цена",
    ],
    title: "Интернет-магазин GODSFORGE — кейс HAY.agency",
    description:
      "Кейс HAY.agency: интернет-магазин GODSFORGE с коллекциями, страницами изделий, авторской историей бренда, корзиной и личным кабинетом.",
    h1: "Разработка интернет-магазина ювелирного бренда GODSFORGE",
    slug: "godsforge",
    canonicalUrl: `${SITE_URL}/cases/godsforge`,
    ogTitle: "Интернет-магазин ювелирного бренда GODSFORGE",
    ogDescription:
      "Каталог коллекций, карточка изделия, история мастера, корзина и личный кабинет для ювелирного бренда.",
    ogImage: `${SITE_URL}${dir}/godsforge-og.webp`,
    robotsIndex: true,
    datePublished: "2024-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка интернет-магазинов",
      to: routes.ecom,
      note: "Каталог, карточка товара и корзина",
    },
    relatedIndustry: {
      label: "Разработка сайтов для ритейла и аксессуаров",
      to: routes.sites,
      note: "Премиальные каталоги и карточки товара",
    },
    relatedCases: [
      {
        label: "Интернет-магазин профессиональной косметики Quantum Shop",
        to: "/cases/quantum-shop",
        note: "Мультибрендовый каталог и карточка товара",
      },
      {
        label: "Интернет-магазин цветов «Цветочный маркет»",
        to: "/cases/cvmarket",
        note: "Каталог, карточка товара и корзина",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Давайте обсудим, как мы можем масштабировать ваш бизнес",
    text: "Оставьте заявку — свяжемся в течение двух часов, чтобы задать вопросы и обсудить задачу.",
    button: "Обсудить премиальный интернет-магазин",
  },

  client: {
    name: "GODSFORGE",
    url: "https://godsforge.ru/",
    city: "Москва",
    industry: "Ювелирные изделия",
  },
  year: "2024",
  services: [
    "Дизайн интернет-магазина",
    "Разработка каталога коллекций",
    "Карточка изделия и корзина",
    "Страница мастера и истории бренда",
    "Личный кабинет",
  ],
  platform: "1С-Битрикс",
  tags: [
    { label: "Интернет-магазин", to: routes.ecom },
    { label: "Разработка", to: routes.ecom },
  ],
  facts: [
    { label: "Клиент", value: "GODSFORGE" },
    { label: "Отрасль", value: "Ювелирные изделия" },
    { label: "Тип проекта", value: "Интернет-магазин ювелирного бренда" },
    { label: "Платформа", value: "1С-Битрикс" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Каталог с базовыми талисманами, коллекционными, индивидуальными и мужскими изделиями",
      "Отдельная страница мастера и смысла коллекций",
      "Карточка изделия с крупными предметными фотографиями и вариантами",
      "Разделы доставки и оплаты, гарантии и рекомендаций по уходу",
      "Корзина и личный кабинет покупателя",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "GODSFORGE — ювелирный бренд с несколькими линейками изделий: базовые талисманы, коллекционные и индивидуальные вещи, отдельная мужская линия. Нужен был интернет-магазин, который выдерживает крупные предметные фотографии и одновременно доносит авторскую историю бренда.",
        "Вторая часть задачи — стандартный e-commerce сценарий: карточка изделия, корзина, оформление заказа и личный кабинет, без которых премиальный каталог не доводит посетителя до покупки.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: тёмная премиальная подложка вместо белого каталога",
    paragraphs: [
      "Интерфейс собран на глубокой нейтральной подложке из палитры бренда — украшения читаются как ювелирные изделия, а не товары маркетплейса.",
      "Крупная предметная фотография остаётся главным элементом карточки: детали, материал и вариант изделия видны без увеличения.",
    ],
    bullets: [
      "Тёмная нейтральная подложка вместо чистого белого",
      "Крупные предметные фотографии в карточке изделия",
      "Отдельная страница мастера как часть покупательского пути",
    ],
  },

  identity: {
    fonts: [
      { name: "Kudryashev Headline", role: "Заголовки", sample: "Коллекция GODSFORGE" },
      { name: "Gilroy", role: "Интерфейс и описания", sample: "Карточка изделия" },
    ],
    colors: [
      { hex: "#15130F", name: "Фон бренда" },
      { hex: "#B08D57", name: "Акцент" },
      { hex: "#F2EEE6", name: "Текст на тёмном" },
    ],
  },

  cover: img(
    "godsforge-case-cover",
    "Главная страница интернет-магазина ювелирного бренда GODSFORGE",
    6,
  ),

  gallery: [
    img("godsforge-catalog", "Каталог коллекций GODSFORGE по типам изделий", 6),
    img("godsforge-product-cart", "Карточка изделия и корзина в интернет-магазине GODSFORGE", 3),
    img("godsforge-brand", "Страница мастера и истории коллекции GODSFORGE", 3),
  ],

  responsive: {
    text: [
      "Мобильный сценарий повторяет десктопный путь: каталог, изделие, корзина и оформление в одной последовательности экранов.",
      "Состояния корзины и карточки изделия в мобильной версии показаны заполненными, как при реальной покупке.",
    ],
    images: [
      mobile("godsforge-mobile-home", "Мобильная главная интернет-магазина GODSFORGE"),
      mobile("godsforge-mobile-catalog", "Мобильный каталог изделий GODSFORGE"),
    ],
  },

  columnBlocks: [
    {
      id: "catalog",
      title: "Каталог и коллекции",
      paragraphs: [
        "Навигация построена по типам изделий и коллекциям: покупатель может двигаться от готового набора талисманов к индивидуальным и коллекционным вещам.",
      ],
      columns: [
        { title: "Базовые изделия", items: ["Талисманы", "Повседневные украшения"] },
        { title: "Коллекции", items: ["Коллекционные изделия", "Индивидуальные заказы"] },
        { title: "Мужская линия", items: ["Мужские украшения", "Аксессуары"] },
      ],
    },
    {
      id: "product",
      title: "Карточка изделия",
      paragraphs: [
        "Карточка собрана вокруг крупного изображения изделия: материал, вариант, цена, кнопка в корзину и связанные изделия из той же коллекции.",
      ],
      bullets: [
        "Крупные предметные фотографии",
        "Выбор варианта и материала",
        "Связанные изделия из коллекции",
      ],
    },
    {
      id: "authorship",
      title: "Авторство и история",
      paragraphs: [
        "Отдельная страница раскрывает историю мастера и смысл коллекций — часть премиальной подачи, а не справочный раздел «О компании».",
      ],
      bullets: ["История мастера", "Смысл и идея коллекций"],
    },
    {
      id: "scenario",
      title: "Покупательский сценарий",
      paragraphs: [
        "Путь от каталога до личного кабинета собран в одну последовательность: каталог → изделие → корзина → оформление и аккаунт.",
      ],
      bullets: [
        "Доставка и оплата",
        "Гарантия и уход за изделием",
        "Личный кабинет и история заказов",
      ],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка интернет-магазина", to: routes.ecom },
      { label: "Дизайн каталога и карточки изделия", to: routes.ecom },
    ],
  },
};
